import { useContext, useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Search, X, MonitorSmartphone, Plus } from "lucide-react";
import { CreateEstimationContext } from "@/context/create-estimation-context";
import { EstimateStatusBadge } from "@/components/dashboard/EstimateStatusBadge";
import { EstimateStatus, IN_PROGRESS_STATUSES, CLOSED_STATUSES } from "@rebuild/shared";
import { AssigneeDropdown } from "@/components/dashboard/AssigneeDropdown";
import { PersonBadge } from "@/components/dashboard/PersonBadge";
import { setGlobalDesktopOverride } from "@/hooks/useIsMobile";
import { DateBadge } from "@/components/dashboard/DateBadge";
import { useDashboard } from "./hooks/useDashboard";
import { MobileAppDownloadPrompt } from "@/components/modals/MobileAppDownloadPrompt";
type UmbrellaStatusCategory = "all" | "in-progress" | "closed";

const FILTER_OPTIONS = [
  { label: "All", value: "all" },
  { label: "In Progress", value: "in-progress" },
  { label: "Completed", value: "closed" },
];

export const MobileDashboardPage = () => {
  const ctx = useContext(CreateEstimationContext);

  const {
    isLoading,
    estimations,
    onClickRow,
    currentAssignee,
    currentProjectName,
    currentSearchStatus,
    searchByUser,
    searchByProject,
    searchByStatus,
  } = useDashboard();

  const getCurrentStatusFilter = (): UmbrellaStatusCategory => {
    if (!currentSearchStatus || currentSearchStatus.length === 0 || typeof currentSearchStatus === "string") return "all";

    // Check if current search status array matches in-progress statuses
    const isInProgress =
      currentSearchStatus.every((status) => IN_PROGRESS_STATUSES.includes(status as EstimateStatus)) &&
      IN_PROGRESS_STATUSES.some((status) => currentSearchStatus.includes(status));

    // Check if current search status array matches closed statuses
    const isClosed =
      currentSearchStatus.every((status) => CLOSED_STATUSES.includes(status as EstimateStatus)) &&
      CLOSED_STATUSES.some((status) => currentSearchStatus.includes(status));

    if (isInProgress) return "in-progress";
    if (isClosed) return "closed";
    return "all";
  };

  const [statusFilter, setStatusFilter] = useState<UmbrellaStatusCategory>(getCurrentStatusFilter());

  // Handle status filter changes
  const handleStatusChange = (newStatus: UmbrellaStatusCategory) => {
    setStatusFilter(newStatus);

    if (newStatus === "all") {
      searchByStatus([]);
    } else if (newStatus === "in-progress") {
      searchByStatus(IN_PROGRESS_STATUSES);
    } else if (newStatus === "closed") {
      searchByStatus(CLOSED_STATUSES);
    }
  };

  useEffect(() => {
    setStatusFilter(getCurrentStatusFilter());
  }, [currentSearchStatus]);

  return (
    <div className="flex h-screen w-screen flex-col bg-gray-50">
      <MobileAppDownloadPrompt open={true} />
      {/* ---------- Header ---------- */}
      <header className="sticky top-0 border-b border-gray-300 bg-white p-2">
        <div className="flex flex-col gap-4 px-1 py-2">
          <h1 className="flex items-center justify-between gap-2 text-base font-semibold text-black">
            <img src="/logo-black.svg" alt="Rebuild" className="h-5" />
            <AssigneeDropdown
              onSelect={searchByUser}
              onAllSelect={() => {
                searchByUser(undefined);
              }}
              value={currentAssignee}
            />
          </h1>
          <div className="flex items-center justify-between gap-2">
            <div className="flex gap-2">
              {FILTER_OPTIONS.map(({ label, value }) => (
                <button
                  key={value}
                  onClick={() => handleStatusChange(value as UmbrellaStatusCategory)}
                  className={`rounded-full px-1 py-1 text-xs font-medium ${
                    statusFilter === value ? "!bg-gray-900 text-white" : "bg-gray-100 text-gray-600"
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>
          <div className="flex items-center gap-2">
            <div className="relative flex-1">
              <Search className="absolute top-1/2 left-3 h-4 w-4 -translate-y-1/2 transform text-gray-400" />
              <input
                type="text"
                placeholder="Search projects..."
                value={currentProjectName || ""}
                onChange={(e) => searchByProject(e.target.value)}
                className="w-full rounded-md border border-gray-300 bg-gray-50 py-2 pr-10 pl-10 text-sm focus:border-gray-400 focus:ring-0"
              />
              {currentProjectName && (
                <button
                  onClick={() => searchByProject("")}
                  className="absolute top-1/2 right-3 -translate-y-1/2 transform text-gray-400 hover:text-gray-600"
                >
                  <X className="h-3 w-3" />
                </button>
              )}
            </div>
            {/**
             * The Desktop View button converts the app to desktop view no
             * matter the device. (a fail-safe for if we guess the wrong device)
             */}
            <button onClick={() => setGlobalDesktopOverride(true)}>
              <MonitorSmartphone className="h-5 w-5" />
            </button>
          </div>
        </div>
      </header>

      {/* ---------- Project list ---------- */}
      <main className="mt-4 flex-1 overflow-y-auto px-4 pb-28">
        {isLoading && (
          <div className="flex flex-col items-center justify-center py-16">
            <svg className="mb-3 h-8 w-8 animate-spin text-gray-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"></path>
            </svg>
            <span className="text-sm text-gray-500">Loading projects...</span>
          </div>
        )}
        {!isLoading &&
          (estimations ?? []).map((estimate) => (
            <article
              key={estimate.id}
              onClick={() => {
                onClickRow(estimate.id);
              }}
              className="mb-3 rounded-xl border border-gray-200 bg-white p-4 shadow-sm"
            >
              <h2 className="mb-1 text-base font-semibold text-gray-900">
                {estimate.projectName || `New Project ${estimate.estimateReferenceNumber}`}
              </h2>

              <div className="mb-2 flex items-center gap-1 text-sm text-gray-500">
                <EstimateStatusBadge status={estimate.status?.length ? estimate.status : EstimateStatus.RECEIVED} />
              </div>

              <div className="mt-2 flex items-center justify-between border-t border-gray-100 pt-3 text-sm text-gray-400">
                <PersonBadge id={estimate.assignee} />
                <DateBadge date={estimate.dateCreated} />
              </div>
            </article>
          ))}
        {!isLoading && !(estimations ?? []).length && <p className="py-8 text-center text-sm text-gray-400">No results</p>}

        {/* ---------- Add Button ---------- */}
        <Button
          onClick={() => ctx?.toggleShowNewEstimationModal()}
          data-testid="new-estimate-button"
          className="fixed right-8 bottom-8 flex h-16 w-16 items-center justify-center rounded-full !bg-black text-white shadow-lg"
        >
          <Plus className="h-8! w-8!" />
        </Button>
      </main>
    </div>
  );
};
